/**
 * DS v2 design tokens.
 *
 * Atlas is meant to consume `@brisarx/core/tokens` (doc 147 §8.9) — but that
 * package does not exist yet (the doc-02 monorepo is not built). Until it
 * does, the DS v2 token values are inlined here verbatim. **When `@brisarx/
 * core` lands, this file is replaced by a re-export** and any Atlas-only
 * token moves upstream first (the §8.9 hard rule).
 *
 * The token surface is now mode-aware (doc 147 §8.9.6 revised — Atlas is no
 * longer dark-only):
 *   • `dsPalettes`   — mode-dependent colour tokens (text, surfaces, brand,
 *                      semantic, 16 domain colours) × { dark, light }.
 *   • `sharedTokens` — mode-invariant tokens (type families & ramp, line-
 *                      height, spacing, shape, motion, density default, icon
 *                      sizes, focus-ring).
 *   • `graphTokens`  — constellation-rendering tokens, indexed by
 *                      { mode } × { standard, high } graph-contrast level.
 *                      Each cell is contrast-validated by palette.test.ts.
 *
 * `dsTokens` remains as a back-compat alias (= `sharedTokens` ∪
 * `dsPalettes.dark`) so module-load-time consumers (graph rendering, SVG
 * export) keep working until they migrate to live `var(--…)` reads.
 */

export type TokenMap = Record<string, string>;
export type Mode = 'dark' | 'light';
export type GraphContrast = 'standard' | 'high';

/* ─── Mode-invariant tokens (DS v2 spec — same in both modes) ─────────── */

export const sharedTokens: TokenMap = {
  // Type families
  'ff-body': "'Inter', system-ui, sans-serif",
  'ff-display': "'DM Sans', system-ui, sans-serif",
  'ff-mono': "'JetBrains Mono', monospace",
  'ff-editorial': "'Fraunces', Georgia, serif",
  // Aliases used in styles.css (keep in sync with the canonical names above)
  'ff-sans':     "'Inter', system-ui, sans-serif",
  'ff-base':     "'Inter', system-ui, sans-serif",
  'ff-fraunces': "'Fraunces', Georgia, serif",

  // Type ramp
  'fs-xs': '11px',
  'fs-sm': '12px',
  'fs-body': '14px',
  'fs-md': '16px',
  'fs-lg': '20px',
  'fs-xl': '28px',
  'fs-xxl': '36px',

  // Line height
  'lh-body': '1.65',
  'lh-table': '1.65',

  // Spacing rhythm (4px base)
  'sp-xs': '4px',
  'sp-sm': '8px',
  'sp-md': '16px',
  'sp-lg': '24px',
  'sp-xl': '32px',
  'sp-xxl': '48px',
  'sp-row': '14px',

  // Shape
  'r-xs': '4px',
  'r-sm': '8px',
  'r-md': '14px',
  'r-card': '14px',
  'r-lg': '20px',
  'r-pill': '9999px',

  // Motion
  'motion-instant': '0ms',
  'motion-fast': '150ms',
  'motion-std': '240ms',
  'motion-slow': '280ms',
  'motion-enter': '280ms',
  'ease-out': 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  'ease-spring': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  'ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',

  // Focus
  'focus-ring': '2px',

  // Layer badge tokens (L1–L5 × bg/text/border — mode-invariant: opacity-based backgrounds self-adapt)
  'layer-L1-bg':     'rgba(139,92,246,0.2)',
  'layer-L1-text':   '#C4B5FD',
  'layer-L1-border': 'rgba(139,92,246,0.3)',
  'layer-L2-bg':     'rgba(52,211,153,0.12)',
  'layer-L2-text':   '#6EE7B7',
  'layer-L2-border': 'rgba(52,211,153,0.25)',
  'layer-L3-bg':     'rgba(251,191,36,0.12)',
  'layer-L3-text':   '#FDE68A',
  'layer-L3-border': 'rgba(251,191,36,0.25)',
  'layer-L4-bg':     'rgba(59,130,246,0.12)',
  'layer-L4-text':   '#93C5FD',
  'layer-L4-border': 'rgba(59,130,246,0.25)',
  'layer-L5-bg':     'rgba(107,114,128,0.18)',
  'layer-L5-text':   '#D1D5DB',
  'layer-L5-border': 'rgba(107,114,128,0.3)',

  // Density default (Atlas overrides to 'compact' via overrides.ts)
  'density-default': 'medium',

  // Icon size scale (doc 147 §8.10.2)
  'icon-xs': '14px',
  'icon-sm': '16px',
  'icon-md': '18px',
  'icon-lg': '24px',
  'icon-xl': '32px',
};

/* ─── Mode-dependent palettes ─────────────────────────────────────────── */

export const dsPalettes: Record<Mode, TokenMap> = {
  dark: {
    // Brand
    brand: '#7C3AED',
    'brand-subtle': '#A875F5',
    'brand-dim': 'rgba(124,58,237,0.15)',

    // Text — 3 tiers, all ≥3:1 vs s0 (text/text2 also ≥4.5:1)
    text: '#F8FAFC',
    text2: '#94A3B8',
    text3: '#475569',

    // Surfaces (4 elevation steps). s3 corrected to DS v2 spec (#35343C).
    s0: '#0E0D13',
    s1: '#18171F',
    s2: '#201F28',
    s3: '#35343C',

    // Borders
    border: 'rgba(248,250,252,0.08)',
    'border-s': 'rgba(248,250,252,0.16)',

    // Semantic (DS v2 dark)
    info: '#06B6D4',
    good: '#10B981',
    caution: '#F59E0B',
    warn: '#F59E0B',    // alias: styles.css uses --warn; same value as caution
    danger: '#EF4444',

    // Extended text tiers
    text4: '#334155',   // ghost structural labels (section headers) — intentionally sub-AA

    // Secondary brand accent — active-state text on brand-dim backgrounds
    'brand-2': '#C4B5FD',

    // Base surface alias (constellation canvas & code blocks)
    s: '#0E0D13',

    // Elevation
    'elev-1': 'none',
    'elev-2': '0 0 0 1px rgba(248,250,252,0.08)',
    'elev-3': '0 8px 32px rgba(0,0,0,0.45)',
    'elev-4': '0 12px 40px rgba(0,0,0,0.55)',

    // 22 domain colours (spec §7 — restructured from 16)
    'd-accounts':      '#60A5FA',
    'd-transactions':  '#34D399',
    'd-budgets':       '#A875F5',
    'd-cashflow':      '#F59E0B',
    'd-goals':         '#818CF8',
    'd-bills':         '#FB7185',
    'd-investments':   '#10B981',
    'd-copilot':       '#A78BFA',
    'd-community':     '#2DD4BF',
    'd-connections':   '#94A3B8',
    'd-reports':       '#C084FC',
    'd-settings':      '#64748B',
    'd-features':      '#FB923C',
    'd-security':      '#F87171',
    'd-tax':           '#84CC16',
    'd-business':      '#FBBF24',
    'd-lending':       '#EF4444',
    'd-expat':         '#06B6D4',
    'd-family':        '#4ADE80',
    'd-couples':       '#F472B6',
    'd-messaging':     '#38BDF8',
    'd-subscription':  '#E879F9',
    'd-gamification':  '#A3E635',
  },

  light: {
    // Brand — `brand` stays constant; subtle/dim follow DS v2 light spec.
    brand: '#7C3AED',
    'brand-subtle': '#6D28D9',
    'brand-dim': 'rgba(124,58,237,0.08)',

    // Text (DS v2 light)
    text: '#0F172A',
    text2: '#475569',
    text3: '#94A3B8',

    // Surfaces (DS v2 light)
    s0: '#F8FAFC',
    s1: '#FFFFFF',
    s2: '#F1F5F9',
    s3: '#E2E8F0',

    // Borders
    border: 'rgba(15,23,42,0.08)',
    'border-s': 'rgba(15,23,42,0.16)',

    // Semantic (DS v2 light — darker for AA on white)
    info: '#0891B2',
    good: '#059669',
    caution: '#D97706',
    warn: '#D97706',    // alias: styles.css uses --warn; same value as caution
    danger: '#DC2626',

    // Extended text tiers
    text4: '#94A3B8',   // ghost structural labels — muted on white, matches dark text2

    // Secondary brand accent — active-state text on light brand-dim backgrounds
    'brand-2': '#5B21B6',

    // Base surface alias (constellation canvas & code blocks)
    s: '#F8FAFC',

    // Elevation — softer light-mode shadows
    'elev-1': 'none',
    'elev-2': '0 0 0 1px rgba(15,23,42,0.08)',
    'elev-3': '0 8px 32px rgba(15,23,42,0.10)',
    'elev-4': '0 12px 40px rgba(15,23,42,0.14)',

    // 22 domain colours — light mode (≥3:1 on s0=#F8FAFC, ΔE≥15 pairs)
    'd-accounts':      '#003D80',
    'd-transactions':  '#15803D',
    'd-budgets':       '#4C1D95',
    'd-cashflow':      '#AA6B11',
    'd-goals':         '#1E40AF',
    'd-bills':         '#9F1239',
    'd-investments':   '#034E37',
    'd-copilot':       '#6D28D9',
    'd-community':     '#154E4E',
    'd-connections':   '#475569',
    'd-reports':       '#4E2F1B',
    'd-settings':      '#1F2937',
    'd-features':      '#B45309',
    'd-security':      '#7F1D1D',
    'd-tax':           '#3F6212',
    'd-business':      '#78350F',
    'd-lending':       '#BE185D',
    'd-expat':         '#0E7490',
    'd-family':        '#4D7C0F',
    'd-couples':       '#C41E3A',
    'd-messaging':     '#0284C7',
    'd-subscription':  '#A21CAF',
    'd-gamification':  '#8B2200',
  },
};

/* ─── Graph rendering tokens (mode × contrast) ────────────────────────── */

export const graphTokens: Record<Mode, Record<GraphContrast, TokenMap>> = {
  dark: {
    standard: {
      'graph-bg': '#0E0D13',
      'graph-edge': 'rgba(248,250,252,0.40)',
      'graph-edge-focus': '#A875F5',
      'graph-node-dim': '#64748B',
      'graph-halo': 'rgba(248,250,252,0.55)',
      'graph-outline': 'transparent',
      'graph-label': '#F8FAFC',
    },
    high: {
      'graph-bg': '#0E0D13',
      'graph-edge': 'rgba(248,250,252,0.65)',
      'graph-edge-focus': '#C4B5FD',
      'graph-node-dim': '#94A3B8',
      'graph-halo': 'rgba(248,250,252,0.85)',
      'graph-outline': '#0E0D13',
      'graph-label': '#F8FAFC',
    },
  },
  light: {
    standard: {
      'graph-bg': '#F8FAFC',
      'graph-edge': 'rgba(15,23,42,0.50)',
      'graph-edge-focus': '#6D28D9',
      'graph-node-dim': '#64748B',
      'graph-halo': 'rgba(15,23,42,0.55)',
      'graph-outline': 'transparent',
      'graph-label': '#0F172A',
    },
    high: {
      'graph-bg': '#F8FAFC',
      'graph-edge': 'rgba(15,23,42,0.65)',
      'graph-edge-focus': '#5B21B6',
      'graph-node-dim': '#475569',
      'graph-halo': 'rgba(15,23,42,0.85)',
      'graph-outline': '#F8FAFC',
      'graph-label': '#0F172A',
    },
  },
};

/* ─── Back-compat alias ───────────────────────────────────────────────── */

/**
 * DEPRECATED — use `dsPalettes[mode]` + `sharedTokens` via
 * `cssVariables.applyAppearance()` for any new code. This export remains so
 * the graph-rendering modules (`buildGraph.ts`, `render.ts`, `exportGraph.ts`)
 * can resolve their initial colour map at module load (jsdom tests, SVG
 * export fallback) without crashing while they migrate to live `var(--…)`
 * reads. Fixed to the **dark** palette so v1 behaviour is preserved.
 */
export const dsTokens: TokenMap = { ...sharedTokens, ...dsPalettes.dark };
